/**
 * EVERY `title` SHOWS AT ONCE, IN OUR OWN BUBBLE.
 *
 * The browser shows a `title` attribute only after the pointer has rested on
 * the element for about a second (longer in Safari), and nothing - no CSS, no
 * attribute - shortens that wait. So a reader moving down a column of device
 * ids, release hashes or truncated button labels saw every message arrive late
 * (founder, 2026-10-19: "appears very slowly with delay, fix it").
 *
 * One delegated listener on the document, installed once by `AppShell`, fixes
 * every `title` in a portal without touching the screens that set them: when
 * the pointer enters an element carrying one, the attribute is parked on
 * `data-cib-tip` (so the browser's own tooltip never starts) and the text is
 * drawn in `.cib-tip`, the same bubble as the heading's (i). It goes back when
 * the pointer leaves, presses, or the page scrolls. A touch does nothing: there
 * is no hover to give, exactly as with the native tooltip.
 */
const PARKED = 'data-cib-tip';
const GAP = 6;
/** Below the arrow cursor's tip, clear of its body. */
const CURSOR = 20;
const EDGE = 8;

export function installHoverTips(doc: Document = document): () => void {
  let owner: Element | null = null;
  let bubble: HTMLElement | null = null;

  const hide = (): void => {
    if (owner !== null) {
      const text = owner.getAttribute(PARKED);
      owner.removeAttribute(PARKED);
      // A title React re-rendered while the bubble was up is the newer one: keep it.
      if (text !== null && !owner.hasAttribute('title')) owner.setAttribute('title', text);
    }
    bubble?.remove();
    owner = null;
    bubble = null;
  };

  // At the pointer, as the native tooltip sits: a cell or a row can be far wider than its words.
  const place = (event: PointerEvent, tip: HTMLElement): void => {
    const view = doc.defaultView;
    const width = view?.innerWidth ?? 0;
    const height = view?.innerHeight ?? 0;
    const below = event.clientY + CURSOR;
    tip.style.left = `${Math.max(EDGE, Math.min(event.clientX, width - tip.offsetWidth - EDGE))}px`;
    tip.style.top = `${
      below + tip.offsetHeight > height - EDGE ? event.clientY - GAP - tip.offsetHeight : below
    }px`;
  };

  const over = (event: PointerEvent): void => {
    if (event.pointerType === 'touch') return;
    const target =
      event.target instanceof Element ? event.target.closest(`[title], [${PARKED}]`) : null;
    if (target !== null && target === owner) return;
    hide();
    const text = target?.getAttribute('title');
    if (target == null || !text) return;
    target.setAttribute(PARKED, text);
    target.removeAttribute('title');
    owner = target;
    // A button's own words on its `title` are there for when they are cut off:
    // shown in full, they get no bubble (and, parked, no late native one either).
    if (repeatsVisibleText(target, text)) return;
    bubble = doc.createElement('div');
    bubble.className = 'cib-tip';
    bubble.setAttribute('role', 'tooltip');
    bubble.textContent = text;
    // An open modal is in the top layer: a bubble left in <body> would sit under it.
    (target.closest('dialog[open]') ?? doc.body).append(bubble);
    place(event, bubble);
  };

  const out = (event: PointerEvent): void => {
    if (event.relatedTarget === null) hide();
  };

  doc.addEventListener('pointerover', over);
  doc.addEventListener('pointerout', out);
  doc.addEventListener('pointerdown', hide, true);
  doc.addEventListener('scroll', hide, true);
  return () => {
    hide();
    doc.removeEventListener('pointerover', over);
    doc.removeEventListener('pointerout', out);
    doc.removeEventListener('pointerdown', hide, true);
    doc.removeEventListener('scroll', hide, true);
  };
}

function repeatsVisibleText(target: Element, text: string): boolean {
  if (target.textContent?.trim() !== text.trim()) return false;
  return ![target, ...target.querySelectorAll('*')].some((el) => el.scrollWidth > el.clientWidth);
}
